import { useMemo, type ReactNode } from 'react';
import { Action, Subject } from '@erp/authz';
import type { WorkbookReportOutput } from '@erp/shared-types';
import { useCan } from '@/hooks/useCan';
import type { WorkbookEditor } from './useWorkbookEditor';
import {
  reportDefaultDatasetKey,
  reportLayoutDatasetKeys,
  useWorkbookReportData,
} from './useWorkbookReportData';
import { WorkbookReportCanvas } from './WorkbookReportCanvas';
import { WorkbookReportLibraryButton } from './WorkbookReportLibraryButton';
import { WorkbookReportExport } from './WorkbookReportExport';

/**
 * A Report Output: charts over the Worksheets' Results, laid out in the
 * Definition so it versions and restores with it.
 */
export function WorkbookReport({
  editor,
  output,
  canUpdate,
  runMenu,
  active,
}: {
  editor: Pick<
    WorkbookEditor,
    'workbookId' | 'definition' | 'edits' | 'save'
  > & {
    runs: Pick<WorkbookEditor['runs'], 'data'>;
  };
  output: WorkbookReportOutput;
  canUpdate: boolean;
  /** Runs up to the Worksheets the Report charts; absent without Execute. */
  runMenu?: ReactNode;
  /** Whether the Report's tab is the one in view. */
  active?: boolean;
}) {
  const { definition } = editor;
  // The report library is the query builder's, open to those who can read inventory.
  const [canUseLibrary] = useCan(Action.Read, Subject.Inventory);
  const [canRead] = useCan(Action.Read, Subject.Workbook);
  const { layout } = output;
  const defaultKey = reportDefaultDatasetKey(definition);
  // By content, so an unrelated edit keeps the same keys.
  const keyList = [...reportLayoutDatasetKeys(layout, defaultKey)].join('\n');
  const keys = useMemo(() => new Set(keyList.split('\n')), [keyList]);
  const update = (fn: (latest: WorkbookReportOutput) => WorkbookReportOutput) =>
    editor.edits
      .output(output.id)
      .update((latest) => (latest.kind === 'report' ? fn(latest) : latest));
  const data = useWorkbookReportData({
    workbookId: editor.workbookId,
    definition,
    runs: editor.runs.data,
    keys,
  });
  return (
    <WorkbookReportCanvas
      layout={layout}
      active={active}
      data={data}
      definition={definition}
      actions={
        <>
          {runMenu}
          {canRead && (
            <WorkbookReportExport
              workbookId={editor.workbookId}
              definition={definition}
              output={output}
              data={data}
              save={editor.save}
            />
          )}
          {canUseLibrary && canUpdate && (
            <WorkbookReportLibraryButton
              workbookId={editor.workbookId}
              definition={definition}
              output={output}
            />
          )}
        </>
      }
      heading={{ title: output.title, description: output.description }}
      options={{ paletteId: output.paletteId, filters: output.filters }}
      onOptionsChange={
        canUpdate
          ? (next) =>
              update((latest) => ({
                ...latest,
                paletteId: next.paletteId,
                filters: next.filters,
              }))
          : undefined
      }
      onChange={
        canUpdate
          ? (next) => update((latest) => ({ ...latest, layout: next }))
          : undefined
      }
      onHeadingChange={
        canUpdate
          ? (next) =>
              update((latest) => ({
                ...latest,
                title: next.title,
                description: next.description,
              }))
          : undefined
      }
    />
  );
}
